// The task sheet — the mobile render of the 360px task panel (mobile
// overhaul Phase 0 §0.6; mockup §3.2 E/F). Full-height Sheet: the editable
// title, three meta chips that open the project / date / priority sheets,
// then the same Notes view/edit surface the desktop panel shows. All of the
// notes state and its autosave come from `useTaskPanel`, so there is one
// copy of that logic; only the chrome differs.
//
// Opened by TaskRow at ≤880px, whatever the caller's `notesMode` is.

import { useState, type ReactNode } from 'react';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { NOTES_MAX_LENGTH, parseNotes } from '../../shared/taskNotes';
import { formatDayMonth, Seg, type DropdownItem } from './primitives';
import { Sheet, SheetButton } from './Sheet';
import { PickerSheet } from './PickerSheet';
import { DateSheet } from './DateSheet';
import { PageMenu } from './PageMenu';
import { CheckIcon, DotsIcon, MoveIcon, TrashIcon } from './icons';
import { NotesLineView, PRIORITY_LABELS, useTaskPanel } from './TaskPanel';
import { type ProjectOption, type TaskRowData } from './TaskRow';
import { PHASE_DOTS } from '../lib/phaseDots';

/** Project rows for a picker sheet: every project plus a "No project" exit.
    Shared with TaskRow's row menu so both lists stay identical. */
function projectPickerItems(projects: ProjectOption[]): DropdownItem<string>[] {
  return [
    ...projects.map((p) => ({ value: p.id, label: p.name, sub: p.sub, group: p.group, dotColor: PHASE_DOTS[p.phase] })),
    { value: '', label: 'No project', dotColor: 'transparent' },
  ];
}

/** The three priority rows. PageMenu has no "current" state, so the one in
    force carries a trailing ✓ (mockup §3.1 C rows). */
export function priorityMenuItems(current: number, onPick: (priority: number) => void) {
  return [1, 2, 3].map((p) => ({
    key: String(p),
    label: `${PRIORITY_LABELS[p]}${p === current ? ' ✓' : ''}`,
    onSelect: () => onPick(p),
  }));
}

/** Which secondary sheet is stacked over the task sheet. */
type TaskSheetMenu = null | 'more' | 'project' | 'priority' | 'trash' | 'date';

export function TaskSheet({
  task,
  today,
  projects,
  onChanged,
  onRemoved,
  onClose,
}: {
  task: TaskRowData;
  today: string;
  /** Enables the project chip and "Move to project…"; static chip without it. */
  projects?: ProjectOption[];
  onChanged: () => void;
  onRemoved?: (id: string) => void;
  onClose: () => void;
}) {
  const {
    mode,
    setMode,
    text,
    setText,
    save,
    commitTitle,
    toggle,
    editingTitle,
    setEditingTitle,
    titleDraft,
    setTitleDraft,
    progress,
    typing,
  } = useTaskPanel(task, onChanged);
  const [menu, setMenu] = useState<TaskSheetMenu>(null);

  const done = task.status === 'done';
  const overdue = !done && task.due_date !== null && task.due_date < today;

  const patch = async (fields: Record<string, unknown>): Promise<boolean> => {
    const ok = await api(`/api/admin/tasks/${task.id}`, { method: 'PATCH', json: fields })
      .then(() => true)
      .catch((error) => {
        reportError(error, 'Could not update the task.');
        return false;
      });
    onChanged();
    return ok;
  };

  // Completing from inside the notes closes the sheet first — the row it
  // came from may be dropped by the parent the moment the PATCH lands.
  const complete = async () => {
    const next = !done;
    onClose();
    const ok = await patch({ status: next ? 'done' : 'todo' });
    if (ok && next) onRemoved?.(task.id);
  };

  const trash = () => {
    onClose();
    return api(`/api/admin/tasks/${task.id}`, { method: 'DELETE' })
      .then(() => {
        onRemoved?.(task.id);
      })
      .catch((error) => reportError(error, 'Could not delete the task.'))
      .finally(() => onChanged());
  };

  const dueLabel = !task.due_date
    ? 'No due date'
    : task.due_date === today
      ? 'Today'
      : formatDayMonth(task.due_date);
  const dueTone = overdue ? ' danger' : !done && task.due_date === today ? ' gold' : '';

  const chip = (label: string, tone: string, onPress: (() => void) | null): ReactNode => (
    <button type="button" className={`pill big${tone}`} disabled={!onPress} onClick={onPress ?? undefined}>
      {label}
      {onPress && <span className="caret">▾</span>}
    </button>
  );

  return (
    <>
      <Sheet
        open
        variant="full"
        onClose={onClose}
        context={`Task · ${task.project_name ?? 'No project'}`}
        rightAction={
          mode === 'edit' ? (
            <SheetButton
              tone="accent"
              onClick={() => {
                void save(text);
                setMode('view');
              }}
            >
              Done
            </SheetButton>
          ) : (
            <SheetButton onClick={onClose}>Close</SheetButton>
          )
        }
        footer={
          <div className="tp-actions">
            <div className="tp-foot">
              <span>Autosaves</span>
              <span>
                {mode === 'edit'
                  ? `${text.length.toLocaleString('en-AU')} / ${NOTES_MAX_LENGTH.toLocaleString('en-AU')}`
                  : 'Tap a box to tick'}
              </span>
            </div>
            {/* Hidden in edit mode so the keyboard has the room. */}
            {mode === 'view' && (
              <div className="tp-buttons">
                <button type="button" className="cbtn wide acc" onClick={() => void complete()}>
                  <CheckIcon />
                  {done ? 'Reopen' : 'Complete'}
                </button>
                <button
                  type="button"
                  className="cbtn tp-dots"
                  aria-label="More task actions"
                  onClick={() => setMenu('more')}
                >
                  <DotsIcon />
                </button>
              </div>
            )}
          </div>
        }
      >
        <div className="tp-head">
          {editingTitle ? (
            <input
              className="inline-input tp-title-input"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTitle();
                if (e.key === 'Escape') setEditingTitle(false);
              }}
              onBlur={commitTitle}
              autoFocus
            />
          ) : (
            // The sheet's initial focus would otherwise land on the first
            // meta chip and ring it as if it were chosen; the title takes it.
            <div
              className="t"
              tabIndex={-1}
              data-sheet-autofocus
              onClick={() => {
                setTitleDraft(task.title);
                setEditingTitle(true);
              }}
            >
              {task.title}
            </div>
          )}
        </div>

        <div className="tp-meta">
          {chip(task.project_name ?? 'No project', '', projects ? () => setMenu('project') : null)}
          {chip(dueLabel, dueTone, () => setMenu('date'))}
          {chip(PRIORITY_LABELS[task.priority] ?? 'Normal', task.priority === 1 ? ' gold' : '', () =>
            setMenu('priority'),
          )}
        </div>

        <div className="tp-notes">
          <div className="tp-notes-head">
            Notes
            {progress.total > 0 && (
              <span className="notes-progress">
                {progress.done} of {progress.total} done
              </span>
            )}
            <span className="seg-wrap">
              <Seg
                options={[
                  { value: 'view', label: 'View' },
                  { value: 'edit', label: 'Edit' },
                ]}
                value={mode}
                onChange={setMode}
              />
            </span>
          </div>

          {mode === 'edit' ? (
            <textarea
              className="notes-textarea"
              maxLength={NOTES_MAX_LENGTH}
              value={text}
              placeholder={'## Kit\n- [ ] Batteries charged\n- [ ] Format cards\nGST = 14320 × 10%'}
              onChange={(e) => {
                typing.current = true;
                setText(e.target.value);
              }}
              onBlur={() => void save(text)}
              autoFocus
            />
          ) : text.trim() === '' ? (
            <div className="notes-empty" onClick={() => setMode('edit')}>
              No notes yet — tap to add a checklist
            </div>
          ) : (
            <div className="notes-view">
              {parseNotes(text).map((line) => (
                <NotesLineView key={line.index} line={line} onToggle={toggle} />
              ))}
            </div>
          )}
        </div>
      </Sheet>

      <PageMenu
        open={menu === 'more'}
        onClose={() => setMenu(null)}
        items={[
          ...(projects
            ? [
                {
                  key: 'move',
                  icon: <MoveIcon />,
                  label: 'Move to project…',
                  onSelect: () => setMenu('project'),
                },
              ]
            : []),
          {
            key: 'trash',
            icon: <TrashIcon />,
            label: 'Move to trash',
            tone: 'danger' as const,
            onSelect: () => setMenu('trash'),
          },
        ]}
      />

      <PickerSheet
        open={menu === 'project'}
        title="Project"
        items={projectPickerItems(projects ?? [])}
        value={task.project_id ?? ''}
        searchable
        onSelect={(v) => {
          setMenu(null);
          void patch({ project_id: v === '' ? null : v });
        }}
        onClose={() => setMenu(null)}
      />

      <PageMenu
        open={menu === 'priority'}
        onClose={() => setMenu(null)}
        title="Priority"
        items={priorityMenuItems(task.priority, (priority) => {
          setMenu(null);
          void patch({ priority });
        })}
      />

      <DateSheet
        open={menu === 'date'}
        title="Due date"
        value={task.due_date}
        allowClear
        onPick={(iso) => {
          setMenu(null);
          void patch({ due_date: iso });
        }}
        onClose={() => setMenu(null)}
      />

      <PageMenu
        open={menu === 'trash'}
        onClose={() => setMenu(null)}
        title="Move to trash?"
        items={[
          {
            key: 'confirm',
            icon: <TrashIcon />,
            label: 'Move to trash',
            tone: 'danger' as const,
            onSelect: () => {
              setMenu(null);
              void trash();
            },
          },
          { key: 'cancel', label: 'Cancel', onSelect: () => setMenu(null) },
        ]}
      />
    </>
  );
}
